import { styled } from '@mui/material/styles';
import {
  Box,
  IconButton,
  Stepper,
  Step,
  StepButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const PREFIX = 'ReservationDialogHeader';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  htop: `${PREFIX}-htop`,
  hbottom: `${PREFIX}-hbottom`,
  title: `${PREFIX}-title`,
  passenger: `${PREFIX}-passenger`,
  stepper: `${PREFIX}-stepper`,
  steps: `${PREFIX}-steps`,
  ofstep: `${PREFIX}-ofstep`,
};

const Root = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    padding: 0,
  },

  [`& .${classes.header}`]: {
    maxWidth: 'calc(1152px + 120px)',
    margin: '0 auto',
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    lineHeight: theme.spacing(5),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
    },
    '.MuiStepIcon-root.Mui-completed': {
      color: '#2E7D32',
    },
  },

  [`& .${classes.htop}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '.MuiIconButton-root': {
      padding: theme.spacing(0.6875),
      [theme.breakpoints.down('md')]: {
        padding: 0,
        ' svg': {
          fontSize: '2rem',
        },
      },
    },
  },

  [`& .${classes.hbottom}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(14.75),
    },
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
      paddingRight: 0,
      marginTop: theme.spacing(0.625),
    },
    marginBottom: 0,
    marginTop: theme.spacing(-1.5),
    fontFamily: 'Bitter',
  },

  [`& .${classes.passenger}`]: {
    textTransform: 'uppercase',
    fontSize: 16,
    fontWeight: 600,
  },

  [`& .${classes.stepper}`]: {
    backgroundColor: '#fff',
    maxWidth: '458px',
    borderRadius: 100,
    paddingTop: theme.spacing(3.5),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(3.5),
    paddingLeft: theme.spacing(4),
  },

  [`& .${classes.steps}`]: {
    display: 'flex',
    alignSelf: 'center',
    alignItems: 'end',
    paddingLeft: 0,
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },

  [`& .${classes.ofstep}`]: {
    color: theme.palette.lightActionDisabled60p.main,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  [`& .${classes.title}`]: {
    paddingLeft: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      paddingLeft: 0,
    },
  },
}));
interface ReservationDialogHeaderProps {
  passengerName?: string;
  title: string;
  steps: string[];
  activeStep: number;
  isValid: boolean;
  step: (step: number) => () => void;
  open: () => void;
}

export default function ReservationDialogHeader({
  passengerName,
  title,
  steps,
  activeStep,
  isValid,
  step,
  open,
}: ReservationDialogHeaderProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Root className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.htop}>
          <IconButton
            onClick={open}
            aria-label="close"
            data-testid="close"
            size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box className={classes.hbottom}>
          <Box>
            {passengerName && (
              <StyledTypography variant="h6" className={classes.passenger}>
                {passengerName}
              </StyledTypography>
            )}
            <StyledTypography
              variant="h1"
              className={classes.title}
              id="reservation-dialog-title">
              {title}
            </StyledTypography>
          </Box>
          {isLarge && (
            <Stepper
              nonLinear
              activeStep={activeStep}
              className={classes.stepper}
              sx={{ fontSize: '16px' }}>
              {steps.map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepButton
                    color="inherit"
                    disabled={index < 3 && !isValid}
                    onClick={step(index)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}
          {!isLarge && (
            <Box className={classes.steps}>
              <Typography variant="subtitle2" component="span">
                {t('reservation.curstep', { step: activeStep + 1 })}
                <span>&nbsp;</span>
              </Typography>
              <Typography
                variant="subtitle2"
                component="span"
                className={classes.ofstep}>
                {t('reservation.ofstep', { steps: steps.length })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Root>
  );
}
